import { all, call, put, select, take, spawn } from 'redux-saga/effects'
import moment from 'moment'
import * as Terra from 'terra-react'

import { ActiveMinutes, WellnessCenter } from '@dialogue/services'
import Config from 'APP/Config'
import { createActiveMinutes, selectTerraReferenceId } from './ActiveMinutesSagas'
import { Connections, getUserId as getTerraUserId } from 'terra-react'
import ActiveMinutesActions, { ActiveMinutesTypes } from 'APP/Redux/ActiveMinutesRedux'
import { logger } from 'APP/Lib/Datadog'
import type { RootState } from 'APP/Store/CreateStore'

export const connectedTrackersState = (state: RootState) => state.activeMinutes.connectedTrackers

const AppleHealtKitPermissions = [
  Terra.CustomPermissions.ACTIVITY_SUMMARY,
  Terra.CustomPermissions.ACTIVE_DURATIONS,
  Terra.CustomPermissions.EXERCISE_DISTANCE,
  Terra.CustomPermissions.STEPS,
  Terra.CustomPermissions.WORKOUT_TYPES,
]

export const SupportedTerraTrackers = {
  [ActiveMinutes.Types.TrackerName.AppleHealth]: {
    connection: Terra.Connections.APPLE_HEALTH,
    permissions: AppleHealtKitPermissions,
  },
  // TODO: remove this entry needed to support TT Legacy flow once the rework is completed
  // TT Tracker names !== Terra Provider names
  [WellnessCenter.Trackers.Types.TrackerName.AppleHealthkit]: {
    connection: Terra.Connections.APPLE_HEALTH,
    permissions: AppleHealtKitPermissions,
  },
  [ActiveMinutes.Types.TrackerName.HealthConnect]: {
    connection: Terra.Connections.HEALTH_CONNECT,
    permissions: [
      Terra.CustomPermissions.ACTIVITY_SUMMARY,
      Terra.CustomPermissions.ACTIVE_DURATIONS,
      Terra.CustomPermissions.EXERCISE_DISTANCE,
      Terra.CustomPermissions.STEPS,
      Terra.CustomPermissions.WORKOUT_TYPES,
    ],
  },
}

export function* checkAppleHealthDisconnectedStatus(): Generator<unknown, void> {
  try {
    const { userId } = yield call(getTerraUserId, Connections.APPLE_HEALTH)
    // if terra cannot find the user, we need to reconnect them if they had previously established a connection
    if (!userId) {
      // cross reference with active-minutes API
      yield put(ActiveMinutesActions.getConnectedTrackers())
      yield take([
        ActiveMinutesTypes.GET_CONNECTED_TRACKERS_SUCCESS,
        ActiveMinutesTypes.GET_CONNECTED_TRACKERS_FAILURE,
      ])
    }
  } catch (error) {
    logger.error('Failed to check Apple Health disconnected status', {}, error)
  }
}

export function* initTerra(): Generator<unknown, void> {
  try {
    const terraReferenceId = yield select(selectTerraReferenceId)
    if (!terraReferenceId) {
      throw new Error('Login not completed. Terra reference id is not set.')
    }

    const terraResponseMessage = yield call(Terra.initTerra, Config.TERRA_DEVID, terraReferenceId)
    if (!terraResponseMessage?.success) {
      throw new Error(`SDK call completed with error: ${terraResponseMessage?.error}`)
    }
    yield spawn(checkAppleHealthDisconnectedStatus)
  } catch (error) {
    // also not sure how useful this error is to log because there's nothing in DD
    logger.error('Failed to initialize Terra', {}, error)
  }
}

export function* backfillTerraData(connection: Terra.Connections): Generator<unknown, void> {
  const startDate = moment.utc().subtract(30, 'days').toDate()
  const endDate = moment.utc().toDate()

  try {
    yield all([
      call(Terra.getDaily, connection, startDate, endDate),
      call(Terra.getActivity, connection, startDate, endDate),
    ])
  } catch (error) {
    logger.error('Failed to backfill Terra data', { connection }, error)
  }
}

// terra connection is happening as a detached process alongside a TT connection
// none of the terra erros would be communicated to the user nor they would affect the TT connection
export function* initTerraConnection({
  trackerName,
  passThroughErrors = false,
}: {
  trackerName: string
  passThroughErrors: boolean
}): Generator<any, void, any> {
  try {
    yield call(initTerra)

    const TerraTrackerConfig =
      SupportedTerraTrackers[trackerName as keyof typeof SupportedTerraTrackers]
    if (!TerraTrackerConfig || !Config.TERRA_DEVID) return

    const activeMinutes = yield call(createActiveMinutes)
    const result = yield call(activeMinutes.authenticateTerra)

    if (!result?.data?.token) {
      logger.error('Terra token creation failed.', { status: result?.data?.status })
      return
    }

    const message = yield call(
      Terra.initConnection,
      TerraTrackerConfig.connection,
      result?.data?.token,
      true,
      TerraTrackerConfig.permissions
    )

    if (message?.success) {
      yield call(backfillTerraData, TerraTrackerConfig.connection)
    } else {
      throw new Error(`Terra.initConnection() failed: ${message?.error}`)
    }
  } catch (error) {
    logger.error('Failed to initialize Terra connection', { trackerName }, error)

    if (passThroughErrors) {
      throw error
    }
  }
}
